// CTV-840: pick a frame of the station's own footage for the TV screen in
// the display ads, and save it as a named still.
//
// The clips offered are the ORIGINALS the station uploaded (intake footage
// and Channel settings clips), never the processed landing video, which is
// darkened and vignetted for the hero. The reader moves through a clip,
// watches the TV preview take the paused frame, names it, and
// saves. Saving captures the frame in the browser, uploads it through a
// signed URL, and registers it on the station; the page then owns the list.
//
// Feedback 2026-09-04: a still can also come from an image the reader
// uploads (a promo graphic, a photo of the anchors). It rides the same path,
// re-encoded like a frame, with no source clip recorded.

import React from 'react';
import { Check, Film, ImagePlus, Loader2, Pause, Play, X } from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import type { DisplayAdStill, PublisherConfig } from '../../../types/selfService';
import { DisplayAdTv } from './DisplayAd';
import type { BannerLook } from './sheetColor';
import { blobToDataUrl, captureImageFile, captureVideoFrame, FRAME_MIME } from './frameCapture';

interface SourceClip {
  storagePath: string;
  url: string;
}

interface FramePickerProps {
  slug: string;
  config: PublisherConfig;
  look: BannerLook;
  onSaved: (stills: DisplayAdStill[], saved: DisplayAdStill) => void;
  onCancel: () => void;
}

const MAX_NAME = 60;
const IMAGE_TYPES = 'image/jpeg,image/png';
/** Where the still comes from: a frame of the station's footage, or an image file. */
type Source = 'footage' | 'image';

/** Chips read "Clip 1", "Clip 2"; the upload's filename is jargon and rides on the tooltip. */
const clipLabel = (_path: string, i: number) => `Clip ${i + 1}`;
const clipFileName = (path: string) => {
  const base = path.split('/').pop() ?? '';
  return base.replace(/^[0-9a-f-]{36}_/i, '');
};

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const FramePicker: React.FC<FramePickerProps> = ({ slug, config, look, onSaved, onCancel }) => {
  const [clips, setClips] = React.useState<SourceClip[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [active, setActive] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [time, setTime] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [frame, setFrame] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [source, setSource] = React.useState<Source>('footage');
  const [image, setImage] = React.useState<{
    blob: Blob;
    dataUrl: string;
    fileName: string;
  } | null>(null);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ clips: SourceClip[] }>(`/stations/${encodeURIComponent(slug)}/display-ad-source-clips`)
      .then((r) => {
        if (!cancelled) setClips(r.clips);
      })
      .catch(() => {
        if (!cancelled) setLoadError("Couldn't load your video. Try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Re-render the preview from the frame the video is paused on. Called
  // when the clip loads, when it pauses, and after a move of the slider;
  // not while playing (feedback 2026-09-04), since only a paused frame can
  // be saved.
  const refreshFrame = React.useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    try {
      setFrame(await blobToDataUrl(await captureVideoFrame(video)));
    } catch {
      // A frame that cannot be read (CORS, not yet decoded) leaves the last one up.
    }
  }, []);

  const seekTo = (t: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setPlaying(false);
    video.currentTime = t;
    setTime(t);
  };
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const pickImage = async (file: File | undefined) => {
    if (!file) return;
    setImageError(null);
    try {
      const blob = await captureImageFile(file);
      setImage({ blob, dataUrl: await blobToDataUrl(blob), fileName: file.name });
    } catch {
      setImage(null);
      setImageError("Couldn't read that picture. Use a JPEG or PNG.");
    }
  };

  const save = async () => {
    const video = videoRef.current;
    const clip = clips?.[active];
    const fromImage = source === 'image';
    if (fromImage ? !image : !video || !clip) return;
    setSaving(true);
    setSaveError(null);
    try {
      let blob: Blob;
      if (fromImage && image) {
        blob = image.blob;
      } else {
        video!.pause();
        setPlaying(false);
        blob = await captureVideoFrame(video!);
      }
      const ticket = await apiClient.post<{ uploadUrl: string; storagePath: string }>(
        `/stations/${encodeURIComponent(slug)}/display-ad-stills/upload-url`,
        { mimeType: FRAME_MIME, sizeBytes: blob.size },
      );
      const put = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': FRAME_MIME },
        body: blob,
      });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      const result = await apiClient.post<{ stills: DisplayAdStill[]; still: DisplayAdStill }>(
        `/stations/${encodeURIComponent(slug)}/display-ad-stills`,
        {
          name: name.trim(),
          storagePath: ticket.storagePath,
          sourceStoragePath: fromImage ? null : clip!.storagePath,
          sourceSeconds: fromImage ? null : video!.currentTime,
        },
      );
      onSaved(result.stills, result.still);
    } catch (e) {
      setSaveError(
        e instanceof Error && /keep \d+ stills/.test(e.message)
          ? e.message
          : "Couldn't save the frame. Try again.",
      );
      setSaving(false);
    }
  };

  const clipList = clips ?? [];
  const clip = clipList[active] ?? null;
  const fromImage = source === 'image';
  const shown = fromImage ? (image?.dataUrl ?? null) : frame;
  const canSave = !!shown && name.trim().length > 0 && !saving;

  const sourceTab = (value: Source, label: string, Icon: typeof Film) => (
    <button
      type="button"
      role="tab"
      aria-selected={source === value}
      onClick={() => setSource(value)}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
        source === value
          ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
          : 'text-slate-600 hover:text-slate-900'
      }`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );

  const footagePane = (
    <div className="flex min-h-[260px] flex-col gap-3">
      {loadError && (
        <p role="alert" className="text-[13px] text-red-700">
          {loadError}
        </p>
      )}
      {clips === null && !loadError && (
        <p className="flex items-center gap-2 text-[13px] text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your video
        </p>
      )}
      {clips && clips.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
          <Film className="h-6 w-6 text-slate-400" aria-hidden />
          <p className="text-[13px] text-slate-600">
            No video yet. Upload your landing video in Channel settings first, or upload a picture
            instead.
          </p>
        </div>
      )}
      {clip && (
        <>
          {clipList.length > 1 && (
            <div
              className="inline-flex flex-wrap gap-0.5 self-start rounded-lg bg-slate-100 p-1"
              role="group"
              aria-label="Clips"
            >
              {clipList.map((c, i) => (
                <button
                  key={c.storagePath}
                  type="button"
                  aria-pressed={i === active}
                  title={clipFileName(c.storagePath)}
                  onClick={() => {
                    setActive(i);
                    setTime(0);
                    setDuration(0);
                    setFrame(null);
                    setPlaying(false);
                  }}
                  className={`rounded-md px-3 py-1 text-[13px] font-medium transition-colors ${
                    i === active
                      ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {clipLabel(c.storagePath, i)}
                </button>
              ))}
            </div>
          )}
          {/* The <video> is the source of truth for the frame; muted so autoplay
              rules do not bite, crossOrigin so the canvas can read it. The
                            slider sits on the video's bottom edge like a player's. */}
          <div className="max-w-3xl overflow-hidden rounded-lg bg-black shadow-sm">
            <video
              key={clip.storagePath}
              ref={videoRef}
              src={clip.url}
              crossOrigin="anonymous"
              muted
              playsInline
              preload="auto"
              className="mx-auto block max-h-[48vh] w-auto max-w-full"
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
              onLoadedData={() => void refreshFrame()}
              onSeeked={(e) => {
                if (e.currentTarget.paused) void refreshFrame();
              }}
              onPause={() => void refreshFrame()}
              onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
              onEnded={() => setPlaying(false)}
            />
            <div className="flex items-center gap-3 bg-slate-900 px-3 py-2">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Play'}
                className="rounded-md p-1.5 text-white hover:bg-white/10"
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <input
                type="range"
                aria-label="Frame"
                min={0}
                max={duration || 0}
                step={0.04}
                value={Math.min(time, duration || 0)}
                onChange={(e) => seekTo(Number(e.target.value))}
                className="flex-1 accent-white"
              />
              <span className="w-20 text-right font-mono text-[12px] text-slate-300">
                {fmt(time)} / {fmt(duration)}
              </span>
            </div>
          </div>
          <p className="text-[12px] text-slate-500">
            Play or drag the slider to the moment you want, then pause. The preview shows the paused
            frame.
          </p>
        </>
      )}
    </div>
  );

  const imagePane = (
    <div className="flex min-h-[260px] flex-col gap-3">
      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_TYPES}
        aria-label="Image file"
        className="sr-only"
        onChange={(e) => {
          void pickImage(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-[13px] text-slate-600 transition-colors hover:border-slate-500 hover:bg-slate-100 hover:text-slate-900"
      >
        {image ? (
          <img
            src={image.dataUrl}
            alt=""
            className="max-h-52 rounded-md object-contain shadow-sm"
          />
        ) : (
          <ImagePlus className="h-6 w-6 text-slate-400" aria-hidden />
        )}
        <span className="font-medium">{image ? image.fileName : 'Choose a Picture'}</span>
        <span className="text-[12px] text-slate-500">
          {image
            ? 'Click to choose a different one'
            : 'JPEG or PNG. Wide pictures fit the TV best.'}
        </span>
      </button>
      {imageError && (
        <p role="alert" className="text-[13px] text-red-700">
          {imageError}
        </p>
      )}
    </div>
  );

  // Feedback 2026-09-04: a modal, sized to the viewport, so the Save button
  // is always on screen; the page's own preview stays put behind it. Centred
  // in the main area, not the window: the backdrop starts past the admin
  // sidebar (w-60 in PublisherAdminLayout), which stays lit.
  return (
    <div
      className="fixed inset-y-0 left-60 right-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add a frame for the TV Screen"
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3">
          <div className="mr-auto">
            <div className="text-[15px] font-semibold text-slate-900">Add a Frame</div>
            <div className="text-[13px] text-slate-500">
              {fromImage
                ? 'Put a picture of your own on the TV.'
                : 'Put a moment from your own video on the TV.'}
            </div>
          </div>
          <div
            role="tablist"
            aria-label="Source"
            className="inline-flex gap-0.5 rounded-lg bg-slate-100 p-1"
          >
            {sourceTab('footage', 'From Your Video', Film)}
            {sourceTab('image', 'Upload a Picture', ImagePlus)}
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-6 overflow-y-auto p-5 lg:grid-cols-[minmax(0,1fr)_400px]">
          {fromImage ? imagePane : footagePane}

          <div className="flex flex-col gap-4 lg:border-l lg:border-slate-100 lg:pl-6">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
              Preview
            </div>
            <div className="flex justify-center rounded-lg bg-slate-100 px-4 py-5">
              <DisplayAdTv config={config} look={look} width={340} screenImageUrl={shown} />
            </div>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium text-slate-700">
                {fromImage ? 'Name this picture' : 'Name this frame'}
              </span>
              <input
                type="text"
                value={name}
                maxLength={MAX_NAME}
                onChange={(e) => setName(e.target.value)}
                placeholder="Like the newscast or the anchors"
                className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
              />
            </label>
            {saveError && (
              <p role="alert" className="text-[13px] text-red-700">
                {saveError}
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!canSave}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                {saving ? 'Saving…' : fromImage ? 'Save Picture' : 'Save Frame'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FramePicker;
